/**
 * The folding behind `CodeBoxes`, as plain functions: what a typed, pasted or
 * linked string becomes before it is a code the game could send. Exported so
 * a game folds a code that arrives in a link exactly as the boxes would.
 */

/**
 * The part of a paste that can be a code: the last non-empty path segment of
 * a link ("https://example.org/coop/ABCDE?x=1" → "ABCDE"), or the trimmed
 * text itself when there is no slash. A query or a fragment after the code is
 * not the code. A game whose links put the code in the fragment
 * ("#/join/CODE") passes its own `extract` instead.
 */
export function extractCode(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed.includes('/')) return trimmed;
  const path = trimmed.split(/[?#]/)[0] ?? trimmed;
  const segments = path.split('/').filter((segment) => segment.trim().length > 0);
  return (segments.at(-1) ?? '').trim();
}

export interface FoldCodeOptions {
  /** How many characters a code has. */
  readonly length: number;
  /** Which single characters survive (after `normalise`). Default: A–Z and 0–9. */
  readonly allowed?: RegExp | undefined;
  /** The part of a paste that can be the code. Default {@link extractCode}. */
  readonly extract?: ((raw: string) => string) | undefined;
  /** Folds look-alikes on the upper-cased text, before filtering. Default: none. */
  readonly normalise?: ((code: string) => string) | undefined;
}

/**
 * `extract`, upper case, `normalise`, keep the `allowed` characters, cut to
 * `length`. Idempotent for any `normalise` that is (folding a folded code
 * changes nothing), which is what lets `CodeBoxes` re-fold a value set from
 * outside without a loop.
 */
export function foldCode(raw: string, options: FoldCodeOptions): string {
  const extract = options.extract ?? extractCode;
  const allowed = options.allowed ?? /[A-Z0-9]/;
  let text = extract(raw).toUpperCase();
  if (options.normalise !== undefined) text = options.normalise(text);
  let out = '';
  for (const char of text) {
    if (out.length >= options.length) break;
    if (allowed.test(char)) out += char;
  }
  return out;
}
